import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API_URL_OPTIONS = [
  'http://localhost:3001/api',
  'http://localhost:3000/api',
  '/api'
];

const FixAgentsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, boolean>>({});
  const [selectedApi, setSelectedApi] = useState<string | null>(null);
  const [fixStatus, setFixStatus] = useState<'idle' | 'fixing' | 'success' | 'error'>('idle');
  const [agentCount, setAgentCount] = useState<number | null>(null);
  const [apiEndpoints, setApiEndpoints] = useState<string[]>(API_URL_OPTIONS);
  const [customApiUrl, setCustomApiUrl] = useState('');
  const [activeTab, setActiveTab] = useState('auto');

  useEffect(() => {
    // Try to get the current API URL from localStorage
    const savedApiUrl = localStorage.getItem('api_url');
    if (savedApiUrl) {
      if (!apiEndpoints.includes(savedApiUrl)) {
        setApiEndpoints([...apiEndpoints, savedApiUrl]);
      }
      setSelectedApi(savedApiUrl);
    }
  }, []);

  const testApiEndpoint = async (endpoint: string) => {
    try {
      console.log(`Testing API endpoint: ${endpoint}`);
      const response = await axios.get(`${endpoint}/agents`, {
        headers: {
          // In development mode, we can use a simple header for testing
          'X-API-TEST': 'true'
        }
      });
      console.log(`Response from ${endpoint}:`, response);
      return {
        success: true,
        count: response.data.agents ? response.data.agents.length : 0
      };
    } catch (error) {
      console.error(`Error testing ${endpoint}:`, error);
      return { success: false, count: 0 };
    }
  };

  const runAllTests = async () => {
    setLoading(true);
    setResults({});
    
    const testResults: Record<string, boolean> = {};
    let firstSuccessfulApi: string | null = null;
    let agentCountFromApi: number | null = null;
    
    for (const endpoint of apiEndpoints) {
      const result = await testApiEndpoint(endpoint);
      testResults[endpoint] = result.success;
      
      if (result.success && !firstSuccessfulApi) {
        firstSuccessfulApi = endpoint;
        agentCountFromApi = result.count;
      }
    }
    
    setResults(testResults);
    setSelectedApi(firstSuccessfulApi);
    setAgentCount(agentCountFromApi);
    setLoading(false);
  };

  const fixApiUrl = () => {
    if (!selectedApi) return;
    
    setFixStatus('fixing');
    
    try {
      // Save the selected API URL to localStorage
      localStorage.setItem('api_url', selectedApi);
      
      // For react-query, we need to invalidate queries
      // This would typically be done by calling queryClient.invalidateQueries()
      // But since we don't have access to the query client here, we'll need to reload
      
      setFixStatus('success');
      
      // After a short delay, reload THIS page so the new api_url is picked up. It used to
      // send the user to /agents, which PR #6308 unmounted (hardcoded mockup, see App.tsx);
      // task-dqgtp8n3o8 owns remounting it.
      setTimeout(() => {
        window.location.href = '/agents/fix-agents';
      }, 1500);
    } catch (error) {
      console.error('Error fixing API URL:', error);
      setFixStatus('error');
    }
  };

  const handleAddCustomUrl = () => {
    if (!customApiUrl) return;
    
    // Validate URL format
    if (!customApiUrl.startsWith('http') && !customApiUrl.startsWith('/')) {
      alert('Please enter a valid URL starting with http://, https://, or /');
      return;
    }
    
    // Add to the list if not already present
    if (!apiEndpoints.includes(customApiUrl)) {
      setApiEndpoints([...apiEndpoints, customApiUrl]);
    }
    
    setCustomApiUrl('');
  };

  return (
    <div className="container mx-auto py-8 max-w-3xl">
      <h1 className="text-3xl font-bold mb-6">Fix API Connection</h1>
      
      <p className="mb-6 text-muted-foreground">
        If you're seeing "Error loading agents" messages, this tool will help diagnose and fix the API connection issues.
      </p>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="auto">Automatic Fix</TabsTrigger>
          <TabsTrigger value="manual">Manual Configuration</TabsTrigger>
        </TabsList>
        
        <TabsContent value="auto" className="pt-4">
          <Card className="p-6">
            <h2 className="text-xl font-bold mb-4">Test API Endpoints</h2>
            
            <Button 
              onClick={runAllTests} 
              disabled={loading}
              className="mb-6"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {loading ? 'Testing...' : 'Test API Endpoints'}
            </Button>
            
            {Object.keys(results).length > 0 ? (
              <>
                <div className="mb-6 space-y-4">
                  <h3 className="text-lg font-semibold">Results:</h3>
                  <ul className="space-y-2">
                    {Object.entries(results).map(([endpoint, success]) => (
                      <li key={endpoint} className="flex items-center gap-2">
                        {success ? 
                          <CheckCircle2 className="h-5 w-5 text-green-500" /> : 
                          <AlertTriangle className="h-5 w-5 text-amber-500" />
                        }
                        <div className="flex-1">
                          <code className="bg-muted px-2 py-1 rounded text-sm">{endpoint}</code>
                        </div>
                        <Button 
                          variant={success ? "outline" : "ghost"} 
                          size="sm"
                          onClick={() => setSelectedApi(endpoint)}
                          disabled={!success}
                          className={selectedApi === endpoint ? "border-primary" : ""}
                        >
                          {selectedApi === endpoint ? "Selected" : success ? "Use This" : "Failed"}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {selectedApi && (
                  <div className="bg-muted p-4 rounded-md">
                    <p className="font-medium">Selected API URL: <code>{selectedApi}</code></p>
                    {agentCount !== null && (
                      <p className="text-sm text-muted-foreground mt-1">This endpoint returned {agentCount} agents</p>
                    )}
                    <Button 
                      onClick={fixApiUrl} 
                      className="mt-4" 
                      disabled={fixStatus === 'fixing'}
                    >
                      {fixStatus === 'fixing' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Apply This Configuration
                    </Button>
                    
                    {fixStatus === 'success' && (
                      <p className="text-green-600 text-sm mt-2">
                        <CheckCircle2 className="inline mr-1 h-4 w-4" />
                        Configuration updated! Redirecting to agents page...
                      </p>
                    )}
                    
                    {fixStatus === 'error' && (
                      <p className="text-red-600 text-sm mt-2">
                        <AlertTriangle className="inline mr-1 h-4 w-4" />
                        Error updating configuration. Please try again.
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : loading ? (
              <div className="flex justify-center items-center h-40">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <p className="ml-2">Testing API endpoints...</p>
              </div>
            ) : null}
          </Card>
        </TabsContent>
        
        <TabsContent value="manual" className="pt-4">
          <Card className="p-6">
            <h2 className="text-xl font-bold mb-4">Manual API Configuration</h2>
            
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Add Custom API URL</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={customApiUrl}
                  onChange={(e) => setCustomApiUrl(e.target.value)}
                  placeholder="http://localhost:3001/api"
                  className="flex-1 border border-input rounded-md px-3 py-2"
                />
                <Button onClick={handleAddCustomUrl}>Add</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Examples: http://localhost:3001/api, /api, https://api.example.com/api
              </p>
            </div>
            
            <div className="mb-6 space-y-4">
              <h3 className="text-lg font-semibold">Available API Endpoints:</h3>
              <ul className="space-y-2">
                {apiEndpoints.map((endpoint) => (
                  <li key={endpoint} className="flex items-center gap-2">
                    <div className="flex-1">
                      <code className="bg-muted px-2 py-1 rounded text-sm">{endpoint}</code>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedApi(endpoint)}
                      className={selectedApi === endpoint ? "border-primary" : ""}
                    >
                      {selectedApi === endpoint ? "Selected" : "Select"}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
            
            {selectedApi && (
              <div className="bg-muted p-4 rounded-md">
                <p className="font-medium">Selected API URL: <code>{selectedApi}</code></p>
                <Button 
                  onClick={fixApiUrl} 
                  className="mt-4" 
                  disabled={fixStatus === 'fixing'}
                >
                  {fixStatus === 'fixing' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Apply This Configuration
                </Button>
                
                {fixStatus === 'success' && (
                  <p className="text-green-600 text-sm mt-2">
                    <CheckCircle2 className="inline mr-1 h-4 w-4" />
                    Configuration updated! Redirecting to agents page...
                  </p>
                )}
                
                {fixStatus === 'error' && (
                  <p className="text-red-600 text-sm mt-2">
                    <AlertTriangle className="inline mr-1 h-4 w-4" />
                    Error updating configuration. Please try again.
                  </p>
                )}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FixAgentsPage;